import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Seo, Mark, Container, StatBand , VideoSlot } from '../components/marketing/Primitives';
import { useVideo } from '../cms/ContentProvider';
import {
  trainingCountryOptions,
  matchDestinations,
  corpusStats,
  corpusFootnote,
  howItWorks,
  stages,
  destinations,
  destinationsNote,
  vaultDocs,
  honesty,
  faq,
} from '../data/pathwaysData';

const selectClass =
  'w-full rounded border border-hairline bg-ink-deep/60 px-3 py-2 text-[13px] text-bright transition-colors focus:border-seal';

/* The interactive match specimen: two real destinations, real routes, the
   product's actual eligibility rules. Change the profile and the cards
   re-sort instantly; ineligible routes stay visible, greyed, with the reason.
   There is no submit button, because the product has none. */
function MatchSpecimen() {
  const [destId, setDestId] = useState('canada');
  const [country, setCountry] = useState('India');
  const [years, setYears] = useState('2+');
  const [english, setEnglish] = useState('test');
  const [specialist, setSpecialist] = useState(false);

  const destination = matchDestinations.find((d) => d.id === destId);
  const profile = { country, years, specialist };

  const { eligible, ineligible } = useMemo(() => {
    const evaluated = destination.routes.map((r) => ({ ...r, ok: r.eligible(profile) }));
    return {
      eligible: evaluated.filter((r) => r.ok).sort((a, b) => a.timeLow - b.timeLow),
      ineligible: evaluated.filter((r) => !r.ok),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destId, country, years, specialist]);

  return (
    <div className="specimen-card !rotate-0">
      <div className="flex items-baseline justify-between gap-4">
        <p className="text-[11px] font-semibold tracking-wide text-slate">Find your route</p>
        <p className="text-[11px] text-faint">Live: change anything, watch the cards</p>
      </div>

      {/* Profile controls */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="ms-dest" className="mb-1 block text-[11px] font-medium text-slate">
            Destination
          </label>
          <select id="ms-dest" value={destId} onChange={(e) => setDestId(e.target.value)} className={selectClass}>
            {matchDestinations.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="ms-country" className="mb-1 block text-[11px] font-medium text-slate">
            Country of medical school / training
          </label>
          <select id="ms-country" value={country} onChange={(e) => setCountry(e.target.value)} className={selectClass}>
            {trainingCountryOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="ms-years" className="mb-1 block text-[11px] font-medium text-slate">
            Years of postgraduate training / practice
          </label>
          <select id="ms-years" value={years} onChange={(e) => setYears(e.target.value)} className={selectClass}>
            <option value="0-1">Fewer than 2</option>
            <option value="2+">2 or more</option>
          </select>
        </div>
        <div>
          <label htmlFor="ms-english" className="mb-1 block text-[11px] font-medium text-slate">
            English proficiency
          </label>
          <select id="ms-english" value={english} onChange={(e) => setEnglish(e.target.value)} className={selectClass}>
            <option value="native">Native / trained in English</option>
            <option value="test">Will take / have a test</option>
          </select>
          <p className="mt-1 text-[10px] leading-snug text-faint">Changes your step list, not your eligibility.</p>
        </div>
      </div>
      <label className="mt-3 flex items-center gap-2 text-[12px] text-slate">
        <input
          type="checkbox"
          checked={specialist}
          onChange={(e) => setSpecialist(e.target.checked)}
          className="h-4 w-4 rounded border-hairline bg-ink-deep accent-[#2dd4bf]"
        />
        I hold a recognized specialist qualification
      </label>

      {/* Results */}
      <p className="mt-5 border-t border-hairline pt-4 text-[12px] font-semibold text-bright">
        {destination.flag} {destination.name}: {eligible.length} of {destination.routes.length} routes match your
        profile
      </p>

      <div className="mt-3 space-y-3">
        {[...eligible, ...ineligible].map((r) => (
          <div
            key={r.id}
            className={`rounded border p-4 transition-opacity ${
              r.ok ? 'border-seal/40 bg-ink-deep/60' : 'border-hairline opacity-60'
            }`}
          >
            <p className={`text-[10px] font-semibold ${r.ok ? 'text-seal' : 'text-faint'}`}>
              {r.ok ? 'You qualify' : 'Not eligible with your profile'}
            </p>
            <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <p className="text-[13px] font-semibold text-bright">{r.name}</p>
              <p className="text-[11px] text-slate">{r.tagline}</p>
            </div>
            <dl className="mt-2.5 grid grid-cols-3 gap-2 text-[11px]">
              <div>
                <dt className="text-faint">Time</dt>
                <dd className="font-semibold text-bright">{r.time}</dd>
              </div>
              <div>
                <dt className="text-faint">Est. cost</dt>
                <dd className="font-semibold text-bright">{r.cost}</dd>
              </div>
              <div>
                <dt className="text-faint">Exams</dt>
                <dd className="font-semibold text-bright">{r.exams}</dd>
              </div>
            </dl>
            <p className="mt-2 text-[11px] leading-snug text-slate">
              <span className="text-faint">Outcome: </span>
              {r.outcome}
            </p>
            <p className="mt-1.5 text-[10px] leading-snug text-faint">{r.notes}</p>
          </div>
        ))}
      </div>

      <p className="mt-4 border-t border-hairline pt-3 text-[10px] leading-snug text-faint">
        Two of the eight destinations, with their real routes, rules, and estimates. Costs and times are route-level
        estimates and exclude living costs, travel, and relocation.
      </p>
    </div>
  );
}

export default function PathwaysPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const demoVideo = useVideo('pathways.compare', null);
  const maxVaultSteps = vaultDocs[0].steps;

  return (
    <>
      <Seo
        title="Pathways"
        description="Every route you qualify for, side by side: licensure pathway comparison for internationally trained physicians, sourced to the regulator, including the routes you cannot take and why. 8 destinations, 15 routes, 81 sourced steps."
      />

      {/* Hero: claim left, working comparison right. */}
      <section className="overflow-hidden bg-ink text-paper-dark-text">
        <Container className="grid items-start gap-14 py-20 md:py-28 lg:grid-cols-[1fr_1.1fr] lg:gap-12">
          <div className="lg:pt-6">
            <Mark dark>Pathways · For internationally trained physicians and their sponsors</Mark>
            <h1 className="hero-rise mt-4 font-serif text-4xl leading-[1.06] md:text-5xl">
              Every route you qualify for. Side by side.
            </h1>
            <p className="hero-rise-2 mt-6 max-w-measure text-lg leading-relaxed text-slate-dark-text">
              Licensure pathway comparison sourced to the regulator, including the routes you cannot take and why.
              Decisions worth years and thousands of dollars deserve better than fragmented, optimistic information.
            </p>
            <p className="hero-rise-2 mt-4 max-w-measure text-sm leading-relaxed text-faint">
              The comparison on the right is live. Change the profile and watch the routes re-sort, including the ones
              that close.
            </p>
            <div className="hero-rise-3 mt-9 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary-on-dark">Compare your routes</Link>
              <a href="#coverage" className="btn-secondary-on-dark">See the coverage</a>
            </div>
          </div>
          <div className="hero-rise-3">
            <MatchSpecimen />
          </div>
        </Container>
      </section>

      {/* Corpus stats */}
      <StatBand stats={corpusStats} footnote={corpusFootnote} />

      {/* How it works */}
      <section className="py-20 md:py-28">
        <Container>
          <Mark>How it works</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            Compare. Commit. Execute.
          </h2>
          <div className="mt-12 grid gap-10 md:grid-cols-3 md:gap-0 md:divide-x md:divide-hairline">
            {howItWorks.map((step, i) => (
              <article key={step.name} className="md:px-8 md:first:pl-0 md:last:pr-0">
                <h3 className="flex items-center gap-3 font-serif text-xl text-bright">
                  <span
                    aria-hidden="true"
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-seal/60 text-sm text-seal"
                  >
                    {i + 1}
                  </span>
                  {step.name}
                </h3>
                <p className="mt-4 leading-relaxed text-slate">{step.text}</p>
              </article>
            ))}
          </div>

          {/* The seven stages, in their dependency order. */}
          <div className="mt-14 rounded-media border border-hairline bg-surface p-6 md:p-8">
            <p className="text-sm font-semibold text-bright">
              Your plan orders its steps by stage, respecting what must happen before what:
            </p>
            <ol className="mt-5 flex flex-wrap items-center gap-y-3">
              {stages.map((s, i) => (
                <li key={s.name} className="flex items-center">
                  <span className="text-sm text-bright">
                    {s.name}
                    <span className="ml-1.5 text-xs text-faint">{s.count}</span>
                  </span>
                  {i < stages.length - 1 && (
                    <span aria-hidden="true" className="mx-3 inline-block h-px w-6 bg-seal/50" />
                  )}
                </li>
              ))}
            </ol>
            <p className="mt-4 text-xs leading-snug text-faint">
              The 81 steps of the corpus, by stage. 38 steps carry prerequisites; 27 are conditional and appear only
              when they apply to you.
            </p>
          </div>
        </Container>
      </section>

      {/* Coverage */}
      <section id="coverage" className="border-y border-hairline bg-ink-deep/40 py-20 md:py-28">
        <Container>
          <Mark>Coverage</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            Eight destinations. Every route published.
          </h2>
          <div className="mt-12 grid gap-px overflow-hidden rounded-media border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-4">
            {destinations.map((d) => (
              <div key={d.name} className="flex flex-col bg-surface p-6">
                <h3 className="font-serif text-lg text-bright">
                  <span aria-hidden="true" className="mr-2">{d.flag}</span>
                  {d.name}
                </h3>
                <dl className="mt-3 space-y-1.5 text-[13px]">
                  <div className="flex justify-between gap-3">
                    <dt className="text-faint">Routes</dt>
                    <dd className="font-semibold text-bright">{d.routes}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-faint">Fastest route</dt>
                    <dd className="font-semibold text-bright">{d.fastest}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-faint">Cheapest route</dt>
                    <dd className="font-semibold text-bright">{d.cheapest}</dd>
                  </div>
                </dl>
                <p className="mt-3 flex-1 text-xs leading-snug text-slate">{d.sub}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 max-w-measure-wide text-sm leading-relaxed text-faint">
            {destinationsNote} 78 jurisdiction records sit underneath: 54 US states and territories, 10 Canadian
            provinces, and the three UAE authorities among them. 87 training countries are recognized in matching,
            driving real branching: five countries skip Australia’s AMC exams entirely, 29 skip New Zealand’s NZREX,
            and seven make a Gulf exam exemption likely.
          </p>
        </Container>
      </section>

      {/* The vault */}
      <section className="py-20 md:py-28">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
            <div>
              <Mark>The document vault</Mark>
              <h2 className="mt-3 max-w-[18ch] font-serif text-3xl leading-tight md:text-4xl">
                Upload once. Satisfy everywhere.
              </h2>
              <p className="mt-5 leading-relaxed text-slate">
                57 of the 81 steps require at least one document. The vault tracks ten document types across every
                journey you ever start: upload a document once and every step that needs it, in every journey, ticks it
                off automatically.
              </p>
              <p className="mt-4 leading-relaxed text-slate">
                The concentration is the point: two documents, your licence certificate and a certificate of good
                standing, unlock 67 step requirements across the corpus. Uploading them is the single
                highest-leverage thing a user does.
              </p>
            </div>
            <div className="self-center rounded-media border border-hairline bg-surface p-6 md:p-8">
              <p className="text-sm font-semibold text-bright">Steps requiring each document</p>
              <ul className="mt-5 space-y-3">
                {vaultDocs.map((doc) => (
                  <li key={doc.name}>
                    <div className="flex items-baseline justify-between gap-4">
                      <p className="text-[13px] text-slate">{doc.name}</p>
                      <p className="text-[13px] font-semibold text-bright">{doc.steps}</p>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-ink-deep">
                      <div
                        className="h-1.5 rounded-full bg-seal/70"
                        style={{ width: `${Math.round((doc.steps / maxVaultSteps) * 100)}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs leading-snug text-faint">
                Three further types are tracked and not yet required by a seeded step: licensing exam results,
                experience letters, and specialist certificates.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Honesty */}
      <section className="border-y border-hairline bg-ink py-20 text-paper-dark-text md:py-28">
        <Container>
          <Mark dark>The honesty no forum or agency will give you</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            Built to tell you the inconvenient part.
          </h2>
          <div className="mt-12 grid gap-x-14 gap-y-10 md:grid-cols-2">
            {honesty.map((h) => (
              <div key={h.title} className="border-t border-hairline-dark pt-5">
                <h3 className="font-serif text-lg text-bright">{h.title}</h3>
                <p className="mt-2 leading-relaxed text-slate-dark-text">{h.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Bridge to Prep */}
      <section className="py-16 md:py-20">
        <Container>
          <div className="flex flex-col items-start justify-between gap-6 rounded-media border border-hairline bg-surface p-8 md:flex-row md:items-center md:p-10">
            <div>
              <h2 className="font-serif text-2xl text-bright">The exam steps on your route are exams Prep teaches.</h2>
              <p className="mt-2 max-w-measure-wide leading-relaxed text-slate">
                USMLE, MCCQE Part I, PLAB / MLA AKT, AMC CAT MCQ: the 16 exam steps in the Pathways corpus map onto the
                licensing exams EdAI Prep prepares you for, from the same certified architecture.
              </p>
            </div>
            <Link to="/products/prep" className="btn-secondary shrink-0">
              Explore EdAI Prep
            </Link>
          </div>
        </Container>
      </section>


      {/* Demo film, published from the content console at /admin */}
      <section className="border-y border-hairline bg-ink py-20 text-paper-dark-text md:py-24">
        <Container>
          <Mark dark>See it</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">Watch a route comparison</h2>
          <p className="mt-4 max-w-measure leading-relaxed text-slate-dark-text">A profile, the side-by-side comparison including greyed ineligible routes, and a committed step plan with the document vault.</p>
          <div className="mt-10 lg:max-w-measure-wide">
            <VideoSlot video={demoVideo} title="Watch a route comparison" text="A profile, the side-by-side comparison including greyed ineligible routes, and a committed step plan with the document vault." />
          </div>
        </Container>
      </section>
      {/* FAQ */}
      <section className="border-t border-hairline py-20 md:py-28">
        <Container>
          <Mark>Questions physicians ask</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">Asked and answered.</h2>
          <div className="mt-10 max-w-measure-wide divide-y divide-hairline border-y border-hairline">
            {faq.map((item, i) => (
              <div key={item.q}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  className="flex w-full items-baseline justify-between gap-6 py-5 text-left"
                >
                  <span className="font-semibold text-bright">{item.q}</span>
                  <span aria-hidden="true" className="text-seal">{openFaq === i ? '\u2212' : '+'}</span>
                </button>
                {openFaq === i && <p className="pb-6 leading-relaxed text-slate">{item.a}</p>}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="border-t border-hairline bg-ink-deep/40 py-20">
        <Container className="text-center">
          <h2 className="mx-auto max-w-[26ch] font-serif text-3xl leading-tight md:text-4xl">
            Years and thousands of dollars are on the table. Compare first.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary">Compare your routes</Link>
          </div>
        </Container>
      </section>
    </>
  );
}
